import styled, { css } from 'styled-components';

export const StyledWSWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledWSTrigger = styled.button<{ $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: var(--r-lg);
  appearance: none;
  background: rgba(255, 255, 255, 0.08);
  color: var(--surface);
  transition: background var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: left;

  > div:first-child {
    border-radius: var(--r-sm);
    background: var(--surface);
    border: none;
    box-shadow: none;
    flex-shrink: 0;
  }

  > div:first-child > img {
    border-radius: 6px;
    object-fit: contain;
    padding: var(--s-1);
  }

  ${({ $collapsed }) =>
    $collapsed &&
    css`
      width: 40px;
      height: 40px;
      padding: 0;
      justify-content: center;
      margin: 0 auto;
      border-radius: var(--r-md);
      background: rgba(255, 255, 255, 0.1);

      > div:first-child {
        background: transparent;
        border-radius: var(--r-sm);
      }

      > div:first-child > img {
        padding: 0;
        border-radius: var(--r-sm);
      }
    `}

  &:hover {
    background: rgba(255, 255, 255, 0.13);
    border-color: rgba(255, 255, 255, 0.16);
  }
`;

export const StyledWSTriggerName = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: var(--surface);
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

export const StyledWSTriggerChevron = styled.span<{ isOpen?: boolean }>`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.4);
  transition: transform var(--dur-base) var(--ease-out);
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg { width: 14px; height: 14px; fill: currentColor; }
`;

export const StyledWSPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 38rem;
  display: flex;
  background: var(--surface);
  border-radius: var(--r-xl);
  border: 1px solid var(--line);
  box-shadow: var(--sh-4);
  overflow: hidden;
  z-index: 400;
`;

export const StyledWSLeftPane = styled.div`
  width: 13.5rem;
  flex-shrink: 0;
  background: var(--paper);
  padding: var(--s-3) var(--s-2);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 24rem;
  overflow-y: auto;
`;

export const StyledWSLeftPaneTitle = styled.span`
  display: block;
  font-size: 10px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: var(--muted-2);
  padding: 0 8px 8px;
`;

export const StyledWSWorkspaceItem = styled.button<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: var(--s-2) var(--s-2);
  border-radius: var(--r-md);
  appearance: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background var(--dur-fast) var(--ease-out);
  background: ${({ isSelected }) => isSelected ? 'var(--surface)' : 'transparent'};
  box-shadow: ${({ isSelected }) => isSelected ? 'var(--sh-1)' : 'none'};

  &:hover { background: var(--surface); }
`;

export const StyledWSWorkspaceAvatar = styled.div<{ color: string; hasLogo: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: var(--r-sm);
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ color, hasLogo }) => (hasLogo ? 'var(--paper-2)' : color)};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: var(--s-1);
  }

  span {
    font-size: 14px;
    font-weight: 800;
    color: var(--surface);
    line-height: 1;
    text-transform: uppercase;
  }
`;

export const StyledWSWorkspaceInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledWSWorkspaceName = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
`;

export const StyledWSWorkspaceCount = styled.span`
  font-size: 11px;
  color: var(--muted-2);
  font-variant-numeric: tabular-nums;
`;

export const StyledWSWorkspaceChevron = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: var(--muted-3);

  svg { width: 12px; height: 12px; }
`;

export const StyledWSRightPane = styled.div`
  flex: 1;
  padding: var(--s-4);
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 24rem;
  overflow-y: auto;
`;

export const StyledWSRightHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const StyledWSRightTitle = styled.span`
  font-size: var(--t-lg);
  font-weight: var(--weight-bold);
  color: var(--ink);
  letter-spacing: var(--tracking-tight);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledWSActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: var(--s-1) var(--s-2);
  border-radius: 999px;
  background: var(--brand-green-soft);
  border: 1px solid rgba(118, 177, 89, 0.28);
  font-size: 11px;
  font-weight: var(--weight-semibold);
  color: var(--brand-green-ink);
  white-space: nowrap;
  flex-shrink: 0;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--brand-green);
    flex-shrink: 0;
  }
`;

export const StyledWSEntityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const StyledWSEntityCard = styled.button<{ isChecked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: var(--s-2) var(--s-3);
  border-radius: var(--r-md);
  appearance: none;
  cursor: pointer;
  text-align: left;
  transition: all var(--dur-fast) var(--ease-out);
  border: 1.5px solid ${({ isChecked }) =>
    isChecked ? 'rgba(118, 177, 89, 0.32)' : 'var(--line)'};
  background: ${({ isChecked }) =>
    isChecked ? 'var(--brand-green-soft)' : 'var(--surface)'};

  &:hover {
    border-color: ${({ isChecked }) =>
      isChecked ? 'rgba(118, 177, 89, 0.5)' : 'var(--line-2)'};
    background: ${({ isChecked }) =>
      isChecked ? 'rgba(118, 177, 89, 0.12)' : 'var(--paper)'};
  }
`;

export const StyledWSEntityDot = styled.span<{ isChecked?: boolean }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 4px;
  background: ${({ isChecked }) =>
    isChecked ? 'var(--brand-green)' : 'var(--muted-3)'};
  transition: background var(--dur-fast) var(--ease-out);
`;

export const StyledWSEntityName = styled.span<{ isChecked?: boolean }>`
  font-size: var(--t-sm);
  font-weight: ${({ isChecked }) => (isChecked ? 600 : 500)};
  color: ${({ isChecked }) => isChecked ? 'var(--ink)' : 'var(--muted)'};
  letter-spacing: -0.01em;
  line-height: 1.35;
`;
